import React from 'react';

interface Props {
  children: React.ReactNode;
}
interface State {
  error: Error | null;
  hasRetried: boolean;
}

// Wraps <Suspense> for lazy-loaded routes. A chunk-load failure throws into
// React; without a boundary the entire app unmounts to a blank page. Most
// common cause: the user has a tab open from before a deploy, the webpack
// chunk hash they're requesting was CDN-purged, and the dynamic import()
// rejects with `ChunkLoadError` (Vite calls it `Failed to fetch dynamically
// imported module`). Auto-retries once (transient network blips), then
// shows a "New version available, reload" prompt on the second failure
// — that's the deploy-staleness case where retry won't help. Adversarial
// review H1 (cross-model consensus).
export default class ChunkErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, hasRetried: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Best-effort log; Sentry may not be initialized yet if this happens
    // in the first 2 seconds. Console keeps the error visible to anyone
    // running with DevTools open.
    // eslint-disable-next-line no-console
    console.error('[ChunkErrorBoundary]', error, info.componentStack);
  }

  isChunkLoadError = (err: Error): boolean => {
    const msg = (err.message || '').toLowerCase();
    return (
      err.name === 'ChunkLoadError'
      || msg.includes('failed to fetch dynamically imported module')
      || msg.includes('loading chunk')
      || msg.includes('importing a module script')
    );
  };

  reload = () => {
    window.location.reload();
  };

  retry = () => {
    // Reset boundary state — React re-renders children which re-attempts
    // the dynamic import. If the same chunk fails again, the boundary
    // catches again and we show the reload prompt.
    this.setState({ error: null, hasRetried: true });
  };

  render() {
    const { error, hasRetried } = this.state;
    if (!error) return this.props.children;

    if (this.isChunkLoadError(error) && !hasRetried) {
      // Auto-retry once on first chunk-load failure.
      window.setTimeout(this.retry, 200);
      return <div className="loading-spinner" style={{ margin: '48px auto' }} />;
    }

    return (
      <div style={{ padding: 32, maxWidth: 480, margin: '64px auto', textAlign: 'center' }}>
        <h2 style={{ marginBottom: 12 }}>
          {this.isChunkLoadError(error) ? 'New version available' : 'Something went wrong'}
        </h2>
        <p style={{ color: 'var(--color-text-secondary)', marginBottom: 16 }}>
          {this.isChunkLoadError(error)
            ? 'A newer version of the app was deployed. Reload to update.'
            : error.message || 'Unknown error.'}
        </p>
        <button
          type="button"
          className="btn-primary"
          onClick={this.reload}
        >
          Reload
        </button>
      </div>
    );
  }
}
